// Connection timeline (device page, column 2): one row per day (Irish time), connected periods
// as bars and when data arrived as ticks underneath, from our gateway's own log; below, the
// connections as a table (start, end, why it ended, records, address, flags).
import { useEffect, useMemo, useState } from 'react';
import { Connection, Connections, trackers } from '../api';
import { dayLabel, duration, irishDate, irishMidnight, shiftDate, time } from '../format';
import { DataGrid } from './DataGrid';

const PERIODS = [['1', 'Today'], ['7', '7 days'], ['10', '10 days']] as const;
const DAY = 86_400_000;

const REASON: Record<string, string> = {
  'closed by tracker': 'tracker hung up', 'closed by Traccar': 'Traccar closed it', 'replaced by a new connection': 'replaced by a new one',
  'socket error': 'network error', 'server error': 'server error', 'silent': 'went silent',
};

export function ConnectionTimeline({ trackerId }: { trackerId: number }) {
  const [days, setDays] = useState<(typeof PERIODS)[number][0]>('10');
  const [data, setData] = useState<Connections | null>(null);
  const [error, setError] = useState('');
  const [pick, setPick] = useState<Connection | null>(null);
  const today = irishDate();
  const firstDay = shiftDate(today, -(Number(days) - 1));
  const from = irishMidnight(firstDay);
  const to = irishMidnight(shiftDate(today, 1));

  useEffect(() => {
    let live = true;
    setPick(null);
    trackers.connections(trackerId, new Date(from).toISOString(), new Date(to).toISOString())
      .then((d) => { if (live) { setData(d); setError(''); } }).catch((e) => live && setError((e as Error).message));
    return () => { live = false; };
  }, [trackerId, from, to]);

  const rows = useMemo(() => Array.from({ length: Number(days) }, (_, i) => shiftDate(today, -i)), [days, today]);
  const now = Date.now();
  const pct = (t: number, d0: number) => `${Math.min(100, Math.max(0, ((t - d0) / DAY) * 100))}%`;

  return (
    <section className="card form conn-box" aria-label="Connection timeline">
      <div className="page-head">
        <h2>Connection timeline</h2>
        <div className="seg" role="group" aria-label="Period">
          {PERIODS.map(([k, label]) => <button key={k} type="button" aria-pressed={days === k} onClick={() => setDays(k)}>{label}</button>)}
        </div>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {!data && !error && <p className="muted">Loading…</p>}
      {data && (
        <>
          <div className="conn-grid" role="list" aria-label="Connected periods per day">
            <div className="conn-hours" aria-hidden="true">
              <span />
              <div>{[0, 6, 12, 18].map((h) => <span key={h} style={{ left: `${(h / 24) * 100}%` }}>{String(h).padStart(2, '0')}</span>)}</div>
            </div>
            {rows.map((d) => {
              const d0 = irishMidnight(d);
              const d1 = irishMidnight(shiftDate(d, 1));
              const spans = data.sessions.filter((s) => Date.parse(s.start) < d1 && (s.end ? Date.parse(s.end) : now) > d0);
              const ticks = data.arrivals.filter(([t]) => t >= d0 && t < d1);
              const up = spans.reduce((sum, s) => sum + Math.max(0, Math.min(d1, s.end ? Date.parse(s.end) : now) - Math.max(d0, Date.parse(s.start))), 0);
              const label = `${dayLabel(`${d}T12:00:00Z`)}: connected ${duration(up / 60_000)}, ${spans.length} connection${spans.length === 1 ? '' : 's'}, data in ${ticks.length} of 144 ten-minute slots`;
              return (
                <div key={d} className="conn-row" role="listitem" aria-label={label}>
                  <span className="conn-day">{dayLabel(`${d}T12:00:00Z`)}</span>
                  <div className="conn-track" title={label}>
                    {spans.map((s) => {
                      const a = Math.max(d0, Date.parse(s.start));
                      const b = Math.min(d1, s.end ? Date.parse(s.end) : now);
                      return (
                        <button key={s.id} type="button" className={`conn-bar${s.flags.length ? ' flagged' : ''}${pick?.id === s.id ? ' on' : ''}`}
                          style={{ left: pct(a, d0), width: `max(2px, ${((b - a) / DAY) * 100}%)` }}
                          aria-label={`Connection ${time(s.start)} to ${s.end ? time(s.end) : 'now'}`} onClick={() => setPick(pick?.id === s.id ? null : s)} />
                      );
                    })}
                    {ticks.map(([t]) => <span key={t} className="conn-tick" style={{ left: pct(t, d0) }} />)}
                  </div>
                </div>
              );
            })}
          </div>
          <ul className="conn-key">
            <li><span className="conn-key-bar" />Connected</li>
            <li><span className="conn-key-bar flagged" />Connection with a warning</li>
            <li><span className="conn-key-tick" />Data received (10-minute slots)</li>
          </ul>
          {data.knownSince && from < Date.parse(data.knownSince) && (
            <p className="muted small">Connections are known from {dayLabel(data.knownSince)} {time(data.knownSince)}, when our gateway started relaying; data received is shown for all days.</p>
          )}
          {pick && (
            <p className="notice small" role="status">
              {time(pick.start)} – {pick.end ? time(pick.end) : 'now'} ({duration(((pick.end ? Date.parse(pick.end) : now) - Date.parse(pick.start)) / 60_000)}) · {pick.records} records
              · from {pick.ip}{pick.endReason && <> · {REASON[pick.endReason] ?? pick.endReason}</>}{pick.flags.length > 0 && <> · <b>{pick.flags.join(', ')}</b></>}
            </p>
          )}
          <details className="conn-list">
            <summary>All connections ({data.sessions.length})</summary>
            <DataGrid id="connections" rows={[...data.sessions].reverse()} rowKey={(s) => s.id} noun={['connection', 'connections']}
              defaultSort={{ key: 'start', dir: -1 }} columns={[
                { key: 'start', label: 'Start', value: (s) => s.start, sortBy: (s) => Date.parse(s.start), cell: (s) => `${dayLabel(s.start)} ${time(s.start)}`, nowrap: true },
                { key: 'end', label: 'End', value: (s) => s.end, sortBy: (s) => (s.end ? Date.parse(s.end) : Infinity), cell: (s) => (s.end ? time(s.end) : 'open'), nowrap: true },
                { key: 'dur', label: 'Length', align: 'right', value: (s) => Math.round(((s.end ? Date.parse(s.end) : now) - Date.parse(s.start)) / 60_000),
                  cell: (s) => duration(((s.end ? Date.parse(s.end) : now) - Date.parse(s.start)) / 60_000) },
                { key: 'records', label: 'Records', align: 'right', value: (s) => s.records },
                { key: 'reason', label: 'Ended', value: (s) => (s.endReason ? REASON[s.endReason] ?? s.endReason : null) },
                { key: 'ip', label: 'Address', value: (s) => s.ip, cell: (s) => <span className="mono">{s.ip}</span> },
                { key: 'flags', label: 'Warnings', value: (s) => s.flags.join(', ') || null },
                { key: 'kb', label: 'Data in (KB)', align: 'right', hidden: true, value: (s) => Math.round(s.bytesIn / 102.4) / 10 },
                { key: 'mode', label: 'Mode', hidden: true, value: (s) => s.mode },
              ]} />
          </details>
        </>
      )}
    </section>
  );
}
